import React from 'react';
import BackToHome from './BackToHome';
import './LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="site-page-topbar legal-page-topbar">
          <BackToHome />
        </div>

        <div className="legal-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: January 25, 2026</p>
          
          <section>
            <h2>1. Introduction</h2>
            <p>
              Cube Solutions LLC ("we," "our," or "us") operates the pycrust.io website and platform 
              (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our Service.
            </p>
          </section>

          <section>
            <h2>2. Platform Access and Permissions</h2>
            <p>
              Pycrust requests access to your connected social platform accounts to enable scheduling and content management features.
            </p>
            
            <h3>2.1 What We Use Your Data For</h3>
            <p>We only use your data to:</p>
            <ul>
              <li><strong>Upload videos and images as assets</strong>: We store your content and publish drafts you create from these assets to your connected platforms at the times you specify</li>
              <li><strong>Manage your scheduled posts</strong>: We track and execute your scheduled content according to your calendar</li>
              <li><strong>Confirm your channel/account connection</strong>: We verify that your accounts are properly connected and accessible</li>
              <li><strong>Display platform status inside your dashboard</strong>: We show connection status and account information in your dashboard</li>
            </ul>
            
            <p>
              We request only the minimum necessary permissions, and you can disconnect accounts at any time. 
              We never sell or share your data.
            </p>

            <h3>2.2 YouTube Permissions</h3>
            <p>
              When you connect your YouTube account, we request the following permission:
            </p>
            <ul>
              <li><strong>https://www.googleapis.com/auth/youtube</strong>: This permission allows us to:
                <ul>
                  <li>Upload videos and YouTube Shorts to your channel</li>
                  <li>Set video metadata (title, description, tags, thumbnails, privacy status)</li>
                  <li>Access your channel information (channel name, channel ID, username handle)</li>
                  <li>Schedule and publish content on your behalf</li>
                </ul>
              </li>
            </ul>
            <p>
              This is a standard YouTube API scope required for content publishing. We do not access your 
              YouTube analytics, comments, or any data beyond what is necessary for scheduling and uploading 
              the content you choose to publish.
            </p>
            <p>
              You can revoke this access at any time by visiting your{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Account Permissions page
              </a>
              {' '}or by disconnecting your YouTube account from within our Service.
            </p>

            <h3>2.3 Instagram Permissions</h3>
            <p>
              When you connect your Instagram account, we request the following permissions:
            </p>
            <ul>
              <li><strong>instagram_business_basic</strong>: View your Instagram profile and basic account information (username, account type)</li>
              <li><strong>instagram_business_content_publish</strong>: Create and publish Reels and other content on your behalf</li>
            </ul>
            <p>
              These permissions are required by Instagram's 2024 API to enable automatic posting and content management. 
              We use these permissions only to:
            </p>
            <ul>
              <li>Verify your account is a Business or Creator account (required for Reels publishing)</li>
              <li>Publish Reels and content you schedule through our platform</li>
              <li>Display your account information in your dashboard</li>
            </ul>
            <p>
              We do not use Instagram data for analytics, advertising, audience creation, or any purpose outside of 
              providing the core scheduling and publishing functionality you choose to use.
            </p>
            <p>
              You can revoke these permissions at any time by visiting your{' '}
              <a
                href="https://www.instagram.com/accounts/manage_access/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram Account Settings → Apps and Websites
              </a>
              {' '}or by disconnecting your Instagram account from within our Service.
            </p>
          </section>

          <section>
            <h2>3. Information We Collect</h2>
            <h3>3.1 Personal Information</h3>
            <p>We collect personal information that you provide directly to us or through third-party authentication:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and profile picture (collected via Google OAuth authentication)</li>
              <li><strong>User Role:</strong> Whether you are a "manager" or "client" (content creator)</li>
              <li><strong>Account Credentials:</strong> OAuth tokens and access credentials for connected social media platforms</li>
              <li><strong>Platform Account Data:</strong> Platform account identifiers, account hashes, and connection status</li>
              <li><strong>Subscription Information:</strong> Subscription tier, billing information (processed through Stripe), and payment history</li>
              <li><strong>Workspace Information:</strong> Workspace memberships, permissions, and collaboration data</li>
            </ul>

            <h3>3.2 Content Data</h3>
            <p>We collect and store content you upload to our Service:</p>
            <ul>
              <li><strong>Uploaded content:</strong> Content you upload, stored in Google Cloud Storage</li>
              <li><strong>Video Metadata:</strong> Titles, descriptions, thumbnails, scheduled timestamps, and other metadata associated with your content</li>
              <li><strong>Organization Data:</strong> Folder structures and organization preferences for your content library</li>
            </ul>

            <h3>3.3 Usage and Operational Data</h3>
            <p>We collect operational data necessary to provide and improve our Service:</p>
            <ul>
              <li><strong>Usage Metrics:</strong> Daily operation counts (schedules, uploads, draft saves), storage usage, and client-manager relationship data</li>
              <li><strong>Session Data:</strong> Authentication session information (stored in cookies for up to 30 days)</li>
              <li><strong>Service Logs:</strong> Basic operational logs for troubleshooting and service improvement</li>
            </ul>

            <h3>3.4 Cookies and Tracking</h3>
            <p>We use cookies solely for authentication and user identification purposes:</p>
            <ul>
              <li><strong>Session Cookies:</strong> Used to maintain your login session and identify your user permissions</li>
              <li><strong>No Tracking Cookies:</strong> We do not currently use cookies for analytics, advertising, or data tracking purposes</li>
            </ul>
          </section>

          <section>
            <h2>4. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li><strong>Provide the Service:</strong> Process video uploads, schedule content to connected platforms, manage client-manager relationships, and maintain your content library</li>
              <li><strong>Platform Integration:</strong> Connect to and publish content to your connected social media platforms using stored OAuth credentials</li>
              <li><strong>Communication:</strong> Send transactional emails (account invitations, workspace invitations, service updates, billing notifications)</li>
              <li><strong>Service Management:</strong> Enforce rate limits and tier restrictions, track storage usage, and manage account access</li>
              <li><strong>Security:</strong> Authenticate users, maintain secure sessions, and prevent unauthorized access</li>
              <li><strong>Service Improvement:</strong> Analyze operational data to improve platform functionality and user experience</li>
            </ul>
            <p><strong>Future Use:</strong> We may send marketing communications in the future. You will be able to opt out of marketing emails while still receiving essential transactional communications.</p>
          </section>

          <section>
            <h2>5. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            
            <h3>5.1 Manager-Client Relationships</h3>
            <p>If you are a client who has added a manager to your account:</p>
            <ul>
              <li>Your manager will have access to your content library, uploaded videos, and scheduling information</li>
              <li>Your manager can schedule and publish content to your connected platform accounts on your behalf (with your explicit permission)</li>
              <li>You can revoke manager access at any time through your account settings</li>
            </ul>

            <h3>5.2 Third-Party Service Providers</h3>
            <p>We share data with trusted service providers who assist in our operations:</p>
            <ul>
              <li><strong>Google Cloud Storage:</strong> Stores your uploaded video files and thumbnails</li>
              <li><strong>Firestore Database:</strong> Stores your account data, content metadata, and platform connections</li>
              <li><strong>Resend:</strong> Sends transactional emails on our behalf</li>
              <li><strong>Social Media Platforms:</strong> Publishes your content to your connected platforms when you schedule or publish through our Service</li>
            </ul>

            <h3>5.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law or to:</p>
            <ul>
              <li>Comply with legal processes, court orders, or government requests</li>
              <li>Protect our rights, property, or safety, or that of our users</li>
              <li>Prevent fraud or investigate potential violations of our Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Storage and Security</h2>
            <p>We implement security measures to protect your information:</p>
            <ul>
              <li><strong>Encrypted Storage:</strong> Video files are stored in Google Cloud Storage with secure access controls</li>
              <li><strong>Secure Authentication:</strong> OAuth tokens and credentials are stored securely and encrypted</li>
              <li><strong>Session Security:</strong> Session cookies are HTTP-only and use secure transmission protocols</li>
              <li><strong>Access Controls:</strong> User data is isolated and accessible only to authorized users and their designated managers</li>
            </ul>
            <p><strong>Data Breach Notification:</strong> In the event of a data breach that may affect your personal information, we will notify affected users via email as soon as reasonably possible.</p>
            <p><strong>Limitation:</strong> While we implement industry-standard security measures, no method of transmission or storage over the internet is 100% secure. You use our Service at your own risk.</p>
          </section>

          <section>
            <h2>7. Your Rights and Data Control</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> View and update your personal information, account settings, and connected platforms through your account dashboard</li>
              <li><strong>Deletion:</strong> Delete your account at any time, which will result in the prompt deletion of your personal data, uploaded content, and associated information</li>
              <li><strong>Platform Disconnection:</strong> Disconnect your social media platform accounts at any time through your account settings</li>
              <li><strong>Manager Management:</strong> Add or remove managers, and revoke manager access to your content and platforms at any time</li>
              <li><strong>Marketing Opt-Out:</strong> Opt out of marketing communications (when available) while still receiving essential transactional emails</li>
              <li><strong>Data Export:</strong> Request a copy of your data by contacting us at contact@pycrust.io</li>
            </ul>
            
            <h3>7.1 Account and Data Deletion</h3>
            <p>
              When you delete your account, we will permanently delete all of your personal information, uploaded content, 
              and associated data. The only data we retain after account deletion are operational logs used exclusively for 
              bookkeeping purposes. These logs do not contain personally identifiable information.
            </p>
            <p>
              For detailed information about what data is deleted and what logs are retained, please see our{' '}
              <a href="/account-deletion" target="_blank" rel="noopener noreferrer">
                Account and Data Deletion Policy
              </a>.
            </p>
            <p>
              <strong>Account deletion is permanent and cannot be undone.</strong> Please ensure you have backed up any 
              content or data you wish to keep before deleting your account.
            </p>
          </section>

          <section>
            <h2>8. YouTube API Required Disclosure</h2>
            <p>
              Our Service uses YouTube API Services to enable users to connect their YouTube channels, upload videos,
              schedule content, and manage their YouTube presence. By using the YouTube integration, you agree to the{' '}
              <a
                href="https://www.youtube.com/t/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                YouTube Terms of Service
              </a>{' '}
              and the{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
              .
            </p>
            <p>
              We only access, use, and store YouTube data as necessary to provide the features you choose to use within
              the Service. This includes retrieving channel information, uploading videos, updating video metadata,
              generating thumbnails, and scheduling or publishing content on your behalf.
            </p>
            <p>
              If an upload step fails (such as thumbnail processing), our platform may set the video visibility to
              &quot;private&quot; as a safety measure to prevent accidental publication. This occurs only as part of a
              user-initiated upload workflow.
            </p>
            <p>
              You can revoke the Service&apos;s access to your YouTube data at any time by visiting your Google Account
              Permissions page:{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://myaccount.google.com/permissions
              </a>
              .
            </p>
            <p>
              We do not use YouTube data for any purpose other than providing the Service&apos;s core functionality. We
              do not transfer YouTube data to external parties except as required to operate the Service or as required
              by law.
            </p>
          </section>

          <section>
            <h2>9. Instagram Permissions & Revocation</h2>
            <p>
              When you connect an Instagram account, you grant our Service permission to publish content on your behalf
              and access basic account information using the Instagram Graph API.
            </p>
            <p>
              You may revoke these permissions at any time by visiting your Meta Accounts Center and navigating to
              <strong> Settings → Business Integrations</strong>, or by disconnecting your Instagram account from within
              our Service.
            </p>
            <p>
              We do not use Instagram data for analytics, advertising, audience creation, or any purpose outside of
              providing the core scheduling and publishing functionality you choose to use.
            </p>
          </section>

          <section>
            <h2>10. OAuth Token Storage and Security</h2>
            <p>
              We store OAuth refresh tokens securely and encrypted so that we can perform scheduled uploads, publish
              content, and access platform features on your behalf without requiring you to re-authenticate. OAuth
              tokens are used only for the features you explicitly choose to use.
            </p>
            <p>
              We do not store your Google or YouTube password at any time. You may disconnect your platform accounts at
              any time through your account settings or by revoking access from your Google Account Permissions page.
            </p>
          </section>

          <section>
            <h2>11. Third-Party Services</h2>
            <p>Our Service integrates with the following third-party services:</p>
            <ul>
              <li><strong>Google OAuth:</strong> For user authentication and account creation</li>
              <li><strong>YouTube API:</strong> For connecting YouTube accounts and publishing content</li>
              <li><strong>Instagram API:</strong> For connecting Instagram accounts and publishing content</li>
              <li><strong>Google Cloud Storage:</strong> For storing uploaded video files and thumbnails</li>
              <li><strong>Firestore Database:</strong> For storing user data and content metadata</li>
              <li><strong>Resend:</strong> For sending transactional emails</li>
              <li><strong>Stripe:</strong> For payment processing, subscription management, and billing</li>
            </ul>
            <p>These third-party services have their own privacy policies and terms of service. We encourage you to review their policies to understand how they handle your data.</p>
          </section>

          <section>
            <h2>12. Children's Privacy</h2>
            <p>Our Service is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately at contact@pycrust.io.</p>
          </section>

          <section>
            <h2>13. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.</p>
          </section>

          <section>
            <h2>14. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us at:
            </p>
            <p>
              Email: contact@pycrust.io<br />
              Company: Cube Solutions LLC
            </p>
          </section>

          <section className="legal-page-footer-note">
            <p>&copy; 2025 pycrust.io. All rights reserved by Cube Solutions LLC.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
